import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteStatusEnum } from '@app/enums/site-status.enum';
import { SiteServicesComponent } from '@app/v2/components/site-services/site-services.component';
import { SiteServicesFormGroup } from '@app/v2/components/site-services/site-services.form';
import { SiteService } from '@app/v2/services/site/site.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AccessGroupEnum } from '@models/accessGroup';
import { ClinTrakStudySiteViewModel } from '@models/clintrak';
import {
    Address,
    Site,
    SiteCRC,
    SiteInfo,
    SitePayment,
    SiteServices,
    SiteStatus,
    SiteVisitStipend,
    SiteVisitStipendBase,
    createNewSite,
    updateSiteProperties,
} from '@models/site';
import { AdminSiteServices } from '@services/admin/admin-site.service';
import { AdminStudyServices } from '@services/admin/admin-study.sevice';
import { ClinTrakDataService } from '@services/clintrak/clintrak-data.service';
import { CurrentlyEditedCardService } from '@services/currently-edited-card/currently-edited-card.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { ClinTrakDataStateService } from '@services/state-management/clintrak-data-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { transformToSiteDataModel } from '@services/transforms/site-transform';
import { rxjsEffect } from '@utility/utility';
import {
    Subject,
    combineLatest,
    distinctUntilKeyChanged,
    filter,
    finalize,
    iif,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';

@Component({
    selector: 'app-create-edit-site',
    standalone: false,
    templateUrl: './create-edit-site.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./create-edit-site.component.scss'],
})
export class CreateEditSiteComponent implements OnInit, OnDestroy {
    site: Site;
    isUpdate: boolean = false;
    private componentDestroyed$: Subject<boolean> = new Subject();
    private siteServiceV2 = inject(SiteService);

    private readonly siteServicev2 = inject(SiteService);

    isAssistantOrHigher$ = this.userService
        .isRole$([
            AccessGroupEnum.Manager,
            AccessGroupEnum.Lead_Coordinator,
            AccessGroupEnum.Coordinator,
            AccessGroupEnum.Assistant,
        ])
        .pipe(shareReplay());

    constructor(
        private activeRoute: ActivatedRoute,
        private router: Router,
        private siteService: AdminSiteServices,
        public dialog: MatDialog,
        private snackbarService: SnackbarService,
        private stateService: SiteStateService,
        private userService: UserServiceV2,
        private cardService: CurrentlyEditedCardService,
        private location: Location,
        private clinTrakDataService: ClinTrakDataService,
        private clinTrakDataStateService: ClinTrakDataStateService
    ) {}
    public readonly create$ = combineLatest({
        routeParamMap: this.activeRoute.paramMap,
        isAssistantOrHigher: this.isAssistantOrHigher$,
    }).pipe(
        take(1),
        tap((result) => {
            const studyId = +result.routeParamMap.get('id');
            this.site = createNewSite();
            const clinTrakSite: ClinTrakStudySiteViewModel =
                this.router.lastSuccessfulNavigation()?.extras?.state?.event;
            if (clinTrakSite) this.setClinTrakData(clinTrakSite);
            else this.stateService.setNewSite(this.site);
            if (result.isAssistantOrHigher) this.stateService.setUsers(studyId);

            this.site.studyId = studyId;
        })
    );
    public readonly edit$ = combineLatest({
        routeParamMap: this.activeRoute.paramMap,
        isAssistantOrHigher: this.isAssistantOrHigher$,
    }).pipe(
        take(1),
        tap((result) => {
            const siteId = +result.routeParamMap.get('siteId');
            result.isAssistantOrHigher
                ? this.stateService.setSiteAndStudyUsers(siteId)
                : this.stateService.setSite(siteId);
        }),
        switchMap((result) => this.stateService.getSite().pipe(takeUntil(this.componentDestroyed$))),
        tap((site) => (this.site = site))
    );

    public readonly vm$ = this.activeRoute.url.pipe(
        switchMap((url) =>
            iif(
                () => {
                    this.isUpdate = url.filter((x) => x.path === 'edit').length !== 0;
                    return this.isUpdate;
                },
                this.edit$,
                this.create$
            )
        )
    );
    ngOnInit(): void {
        // clear store on page load to ensure no data from previous site is shown while loading a new site, this can be removed when we move to signals and each component only subscribes to the data it needs
        this.stateService.clearStore();
        this.vm$.subscribe();
    }

    setClinTrakData(clinTrakSite: ClinTrakStudySiteViewModel) {
        this.clinTrakDataStateService.setStudySiteData(clinTrakSite);
        if (this.site?.info) {
            this.site.info.name = clinTrakSite.siteName;
            this.site.info.siteNumber = clinTrakSite.studySiteCenterId;
            this.site.info.pilastName = clinTrakSite.primaryInvestigator;
            this.site.info.clintrakStatus = clinTrakSite.statusType;
            this.site.primaryCrcEmailToInvite = clinTrakSite.primaryCrcEmail;

            this.site.status = <SiteStatus>{
                statusId: SiteStatusEnum.Created,
                statusName: SiteStatusEnum.Created.toString(),
            };
        }

        this.clinTrakDataService
            .getStudySiteAddress(clinTrakSite.studySiteId)
            .pipe(
                take(1),
                tap((siteAddress) => {
                    if (this.site?.address) {
                        this.site.address.line1 = siteAddress?.address1;
                        this.site.address.line2 = siteAddress?.address2;
                        this.site.address.city = siteAddress?.city;
                        this.site.address.country = siteAddress?.country;
                        this.site.address.postalCode = siteAddress?.postalCode;
                        this.site.address.stateOrProvince = siteAddress?.stateOrProvinceName;
                        this.site.isShippingAddressSameAsPrimary = true;
                    }
                }),
                finalize(() => this.stateService.setNewSite(this.site))
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
        this.stateService.clearStore();
    }

    detectAddressChange: (event: Address) => void = (event: Address) => {
        if (this.site) this.site.address = updateSiteProperties(event, this.site.address);
    };

    detectShippingAddressChange: (event: Address) => void = (event: Address) => {
        if (this.site) this.site.shippingAddress = updateSiteProperties(event, this.site.shippingAddress);
    };

    detectIsShippingRadioChange: (event: boolean) => void = (event: boolean) => {
        if (this.site) this.site.isShippingAddressSameAsPrimary = event;
    };

    detectServicesChange: (event: SiteServices) => void = (event: SiteServices) => {
        if (this.site) this.site.services = updateSiteProperties(event, this.site.services);
    };

    detectPaymentChange: (event: SitePayment) => void = (event: SitePayment) => {
        if (this.site) this.site.payment = updateSiteProperties(event, this.site.payment);
    };

    detectCRCChange: (event: SiteCRC[]) => void = (event: SiteCRC[]) => {
        if (this.site) this.site.siteCRCs = event;
    };

    detectInfoChange: (event: SiteInfo) => void = (event: SiteInfo) => {
        if (this.site) this.site.info = updateSiteProperties(event, this.site.info);
    };

    detectCrcToInviteChange: (event: string) => void = (event: string) => {
        if (this.site) this.site.primaryCrcEmailToInvite = event;
    };

    detectVisitStipendChange: (event: SiteVisitStipend[]) => void = (event: SiteVisitStipend[]) => {
        if (this.site)
            this.site.siteVisitStipends = event.map(
                (x) =>
                    <SiteVisitStipendBase>{
                        visitId: x.visitId,
                        siteId: x.siteId,
                        amount: x.amount,
                    }
            );
    };

    changeStatus(statusName: string): void {
        if (this.site)
            this.siteService
                .changeSiteStatus(statusName, this.site.id)
                .pipe(
                    take(1),
                    finalize(() => {
                        this.stateService.setSite(this.site.id);
                        this.siteServiceV2.siteOverview.set((current) => ({
                            ...current,
                            statusId: SiteStatusEnum[statusName as keyof typeof SiteStatusEnum],
                        }));
                        this.snackbarService.openInfoSnackbar(`Status changed to ${statusName}`);
                    })
                )
                .subscribe();
    }

    validateAndPost: () => void = () => {
        if (!this.formGroup.valid) {
            this.formGroup.markAllAsTouched();
            return;
        }
        this.site = this.siteServicesCard().push(this.site);
        this.site.siteNotes = this.stateService.getSiteNotesFormData();

        let model = transformToSiteDataModel(this.site);
        this.siteService.createSite(model).subscribe({
            next: (response) => {
                this.location.back();
                this.snackbarService.openInfoSnackbar('Created new site');
            },
        });
    };

    validateAndUpdate: (event: any) => void = (event: any) => {
        this.site = this.siteServicesCard().push(this.site);

        this.site.siteNotes = this.stateService.getSiteNotesFormData();

        let model = transformToSiteDataModel(this.site);
        this.siteService.updateSite(model).subscribe({
            next: (payQuickerErrors) => {
                let card = this.cardService.getState();
                if (card?.cardForm) {
                    card.cardForm.toggleEditMode();
                    this.cardService.clearCurrentState(card);
                }
                this.stateService.setSiteAndStudyUsers(model.id);
                if (event?.type !== 'incrementalSave') {
                    this.location.back();
                }
                this.siteServicev2.siteOverview.clear();
                const updateInfo = `Updated Site ${this.site.info.name}`;

                if (payQuickerErrors?.length > 0) {
                    this.snackbarService.openErrorSnackbar(
                        `${updateInfo} \n Invitation approval request: ${payQuickerErrors.join(', ')}`
                    );
                } else {
                    this.snackbarService.openInfoSnackbar(updateInfo);
                }
            },
        });
    };

    getTitleLabel(): string {
        return this.isUpdate ? 'Edit Site' : 'Create New Site';
    }

    formGroup = new FormGroup({
        siteServices: new SiteServicesFormGroup(),
    });

    siteServicesCard = viewChild.required<SiteServicesComponent>(SiteServicesComponent);
    onSiteServicesSave() {
        if (!this.formGroup.valid) {
            // I put this here instead of inside validateAndUpdate because other cards seem to be updated ignoring the state of formgroup and that would break them
            this.formGroup.markAllAsTouched();
            return;
        }
        this.validateAndUpdate({ type: 'incrementalSave', doRoute: false });
    }

    adminStudyService = inject(AdminStudyServices);
    studyStateService = inject(StudyStateService);
    // add missing study state
    setStudyState$ = rxjsEffect(() =>
        this.stateService.getSite().pipe(
            filter(Boolean),
            distinctUntilKeyChanged('studyId'),
            switchMap((site) =>
                this.adminStudyService
                    .getStudy(site.studyId)
                    .pipe(tap((study) => this.studyStateService.setNewStudy(study)))
            )
        )
    );
}
